/**
 * Analytics Service
 * Comprehensive user behavior tracking with Google Analytics 4
 */

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

// Analytics Events
export enum AnalyticsEvent {
  // Authentication Events
  SIGN_IN_ATTEMPT = 'sign_in_attempt',
  SIGN_IN_SUCCESS = 'sign_in_success',
  SIGN_IN_FAILURE = 'sign_in_failure',
  SIGN_OUT = 'sign_out',
  PASSWORD_RESET_REQUEST = 'password_reset_request',
  PASSWORD_RESET_COMPLETE = 'password_reset_complete',
  
  // Navigation Events
  PAGE_VIEW = 'page_view',
  ROUTE_CHANGE = 'route_change',
  
  // Feature Usage
  DASHBOARD_VIEW = 'dashboard_view',
  PROPERTIES_VIEW = 'properties_view',
  TENANTS_VIEW = 'tenants_view',
  PAYMENTS_VIEW = 'payments_view',
  REPORTS_VIEW = 'reports_view',
  SETTINGS_VIEW = 'settings_view',
  
  // Property Management
  PROPERTY_CREATE = 'property_create',
  PROPERTY_EDIT = 'property_edit',
  PROPERTY_DELETE = 'property_delete',
  PROPERTY_VIEW_DETAILS = 'property_view_details',
  
  // Tenant Management
  TENANT_CREATE = 'tenant_create',
  TENANT_EDIT = 'tenant_edit',
  TENANT_DELETE = 'tenant_delete',
  TENANT_VIEW_DETAILS = 'tenant_view_details',
  
  // Payment Processing
  PAYMENT_RECORD = 'payment_record',
  PAYMENT_EDIT = 'payment_edit',
  PAYMENT_DELETE = 'payment_delete',
  PAYMENT_MATCH = 'payment_match',
  PAYMENT_UNMATCH = 'payment_unmatch',
  PAYMENT_SEARCH = 'payment_search',
  PAYMENT_FILTER = 'payment_filter',
  
  // Gmail Integration
  GMAIL_CONNECT_START = 'gmail_connect_start',
  GMAIL_CONNECT_SUCCESS = 'gmail_connect_success',
  GMAIL_CONNECT_FAILURE = 'gmail_connect_failure',
  GMAIL_DISCONNECT = 'gmail_disconnect',
  GMAIL_SYNC_START = 'gmail_sync_start',
  GMAIL_SYNC_COMPLETE = 'gmail_sync_complete',
  
  // Google Drive Integration
  DRIVE_IMPORT_START = 'drive_import_start',
  DRIVE_IMPORT_SUCCESS = 'drive_import_success',
  DRIVE_IMPORT_FAILURE = 'drive_import_failure',
  
  // Reports & Analytics
  REPORT_GENERATE = 'report_generate',
  REPORT_DOWNLOAD = 'report_download',
  REPORT_FILTER = 'report_filter',
  
  // Settings
  SETTINGS_UPDATE = 'settings_update',
  REMINDER_CONFIGURE = 'reminder_configure',
  LANGUAGE_CHANGE = 'language_change',
  
  // Errors & Blockers
  ERROR_OCCURRED = 'error_occurred',
  API_FAILURE = 'api_failure',
  VALIDATION_ERROR = 'validation_error',
  
  // User Engagement
  SESSION_START = 'session_start',
  SESSION_END = 'session_end',
  FEATURE_INTERACTION = 'feature_interaction',
  FORM_SUBMIT = 'form_submit',
  FORM_ABANDON = 'form_abandon',
  MODAL_OPEN = 'modal_open',
  MODAL_CLOSE = 'modal_close',
  
  // Performance
  PAGE_LOAD_TIME = 'page_load_time',
  API_RESPONSE_TIME = 'api_response_time',
}

// Analytics Parameters
export interface AnalyticsParams {
  user_id?: string;
  category?: string;
  label?: string;
  value?: number;
  error_message?: string;
  error_code?: string;
  page_path?: string;
  page_title?: string;
  feature_name?: string;
  duration?: number;
  status?: 'success' | 'failure' | 'pending';
  method?: string;
  [key: string]: any;
}

class Analytics {
  private isInitialized = false;
  private measurementId: string | null = null;
  private isDebugMode = false;

  /**
   * Initialize Google Analytics 4
   */
  init(measurementId: string, debugMode = false) {
    if (this.isInitialized) return;

    this.measurementId = measurementId;
    this.isDebugMode = debugMode;

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };

    window.gtag('js', new Date() as never);
    window.gtag('config', measurementId, {
      send_page_view: false, // We'll handle page views manually
      debug_mode: debugMode,
    });

    this.isInitialized = true;

    if (debugMode) {
      console.log('[Analytics] Initialized with ID:', measurementId);
    }
  }

  /**
   * Track custom event
   */
  trackEvent(eventName: AnalyticsEvent | string, params?: AnalyticsParams) {
    if (!this.isInitialized || !window.gtag) {
      if (this.isDebugMode) {
        console.warn('[Analytics] Not initialized. Event not tracked:', eventName);
      }
      return;
    }

    const eventParams = {
      ...params,
      timestamp: new Date().toISOString(),
    };

    window.gtag('event', eventName, eventParams);

    if (this.isDebugMode) {
      console.log('[Analytics] Event tracked:', eventName, eventParams);
    }
  }

  /**
   * Track page view
   */
  trackPageView(pagePath: string, pageTitle: string) {
    this.trackEvent(AnalyticsEvent.PAGE_VIEW, {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }

  /**
   * Track user login
   */
  trackLogin(method: 'email' | 'google' = 'email', userId?: string) {
    this.trackEvent(AnalyticsEvent.SIGN_IN_SUCCESS, {
      method,
      user_id: userId,
    });

    if (userId) {
      this.setUserId(userId);
    }
  }

  /**
   * Track user logout
   */
  trackLogout() {
    this.trackEvent(AnalyticsEvent.SIGN_OUT);
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string) {
    if (!this.isInitialized || !window.gtag) return;

    window.gtag('config', this.measurementId!, {
      user_id: userId,
    });
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.isInitialized || !window.gtag) return;

    window.gtag('set', 'user_properties', properties);
  }

  /**
   * Track error/exception
   */
  trackError(error: Error | string, fatal = false, params?: AnalyticsParams) {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorStack = typeof error === 'string' ? undefined : error.stack;

    this.trackEvent(AnalyticsEvent.ERROR_OCCURRED, {
      error_message: errorMessage,
      error_stack: errorStack,
      fatal,
      ...params,
    });
  }

  /**
   * Track form submission
   */
  trackFormSubmit(formName: string, success: boolean, params?: AnalyticsParams) {
    this.trackEvent(AnalyticsEvent.FORM_SUBMIT, {
      form_name: formName,
      status: success ? 'success' : 'failure',
      ...params,
    });
  }

  /**
   * Track form abandonment
   */
  trackFormAbandon(formName: string, fieldsCompleted: number, totalFields: number) {
    this.trackEvent(AnalyticsEvent.FORM_ABANDON, {
      form_name: formName,
      fields_completed: fieldsCompleted,
      total_fields: totalFields,
      completion_rate: (fieldsCompleted / totalFields) * 100,
    });
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint: string, duration: number, status: number, params?: AnalyticsParams) {
    this.trackEvent(AnalyticsEvent.API_RESPONSE_TIME, {
      endpoint,
      duration,
      status_code: status,
      status: status >= 200 && status < 300 ? 'success' : 'failure',
      ...params,
    });
  }

  /**
   * Track page load performance
   */
  trackPageLoadTime(pagePath: string, duration: number) {
    this.trackEvent(AnalyticsEvent.PAGE_LOAD_TIME, {
      page_path: pagePath,
      duration,
      value: duration,
    });
  }

  /**
   * Track feature interaction
   */
  trackFeature(featureName: string, action: string, params?: AnalyticsParams) {
    this.trackEvent(AnalyticsEvent.FEATURE_INTERACTION, {
      feature_name: featureName,
      action,
      ...params,
    });
  }

  /**
   * Start timing an action
   */
  startTimer(timerName: string): () => number {
    const startTime = performance.now();

    return () => {
      const duration = performance.now() - startTime;
      return Math.round(duration);
    };
  }

  /**
   * Track user session
   */
  trackSessionStart(userId?: string) {
    this.trackEvent(AnalyticsEvent.SESSION_START, {
      user_id: userId,
    });
  }

  trackSessionEnd(duration: number) {
    this.trackEvent(AnalyticsEvent.SESSION_END, {
      session_duration: duration,
      value: duration,
    });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// React Hook for analytics
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export function usePageTracking() {
  const location = useLocation();
  const prevPath = useRef<string>();

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    if (prevPath.current !== currentPath) {
      const pageTitle = document.title;
      analytics.trackPageView(currentPath, pageTitle);
      prevPath.current = currentPath;
    }
  }, [location]);
}

export function useSessionTracking(userId?: string) {
  useEffect(() => {
    const sessionStart = Date.now();
    analytics.trackSessionStart(userId);

    return () => {
      const sessionDuration = Date.now() - sessionStart;
      analytics.trackSessionEnd(sessionDuration);
    };
  }, [userId]);
}

export default analytics;
